// Drawola Studio — editor breadcrumb bar. Fork-only.
//
// Sits in the editor's top-left corner, immediately to the right of upstream's
// main-menu (hamburger) island:
//
//     [☰]   ⟨ 🏠 › My flowchart ⟩
//
// It answers the two questions the editor could not answer before the Studio
// existed: "which draw am I in?" and "how do I get back?".
//
// Rendering notes (same constraints as DrawolaStudio.tsx):
//  - The root carries upstream's `.excalidraw` class purely to inherit the
//    design tokens (`--island-bg-color`, `--shadow-island`, `--border-radius-lg`,
//    …) that `packages/excalidraw/css/theme.scss` scopes to that selector, plus
//    `theme--dark` for the dark palette. It is a SIBLING of the editor's
//    `.excalidraw-container`, never a descendant, so both themes come for free.
//  - It renders OUTSIDE `<Excalidraw>`, so i18n goes through `useStudioT()`
//    (see ./i18n.ts) — `useDrawolaT()` would throw, it needs the editor's
//    jotai isolation.
//
// Visibility (see also breadcrumb section of studio.scss):
//  - zen mode / view mode → read from `appState` via `excalidrawAPI.onChange`.
//    That is the least invasive reliable signal available from outside the
//    editor tree: `useEditorInterface()` / `useExcalidrawAppState()` are React
//    contexts that only exist INSIDE `<Excalidraw>` (we would silently get the
//    defaults), and sniffing upstream CSS classes on `.excalidraw-container`
//    would couple us to markup upstream rewrites freely.
//  - Studio overlay open → App.tsx simply does not render us.
//  - Narrow viewports → CSS, see studio.scss.

import { THEME } from "@excalidraw/common";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

import type { Theme } from "@excalidraw/element/types";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { SyncStatusIcon } from "../cloud/sync";
import { getDocMeta, renameDoc } from "../storage/multiFileStore";

import { useStudioT } from "./i18n";

import type { CloudSyncApi } from "../cloud/sync";
import type { DocMeta } from "../storage/types";

/**
 * House glyph. Inline so the breadcrumb depends on no upstream icon name and
 * on no shared app file that an upstream merge re-touches.
 */
export const STUDIO_HOME_ICON = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 20 20"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.5 8.5 10 3l6.5 5.5V16a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V8.5Z" />
    <path d="M8 17v-5h4v5" />
  </svg>
);

const CHEVRON_ICON = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 20 20"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 5l4 5-4 5" />
  </svg>
);

type Props = {
  /** Current app theme, so the bar matches the editor in light and dark. */
  theme: Theme;
  /** Used to observe `appState.zenModeEnabled` / `viewModeEnabled`. */
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  /** Active document in the multi-file store (`useMultiFileStore`). */
  activeDocId: string | null;
  /** Bumped by the store on every write — re-reads the name. */
  refreshToken: number;
  /**
   * App.tsx's `handleGoHome` (leave any live room, flush the pending save,
   * then show the Studio). It stays visible during a session on purpose: it is
   * the way out of a room, and hiding it left anyone who joined by link stuck
   * in one. `undefined` only when the Studio itself is switched off.
   */
  onGoHome?: () => void | Promise<void>;
  /**
   * Called after a successful rename so the store bumps `refreshToken` and the
   * Studio grid / Files sidebar pick the new name up.
   */
  onDocRenamed?: () => void;
  /** Cloud sync state and actions; absent when cloud storage is off. */
  cloudSync?: CloudSyncApi;
};

export const EditorBreadcrumb = ({
  theme,
  excalidrawAPI,
  activeDocId,
  refreshToken,
  onGoHome,
  onDocRenamed,
  cloudSync,
}: Props) => {
  const t = useStudioT();

  // `undefined` = not looked up yet (render nothing rather than flashing the
  // "Untitled" fallback for a frame); `null` = looked up, no record.
  // The whole meta is kept (not just the name) because the sync icon reads the
  // cloud binding off it.
  const [meta, setMeta] = useState<DocMeta | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [chromeHidden, setChromeHidden] = useState(false);

  // Enter/Escape both unmount the input, and browsers fire `blur` on the way
  // out — this latch keeps that trailing blur from re-committing (or, after
  // Escape, from committing at all).
  const settledRef = useRef(false);

  // --- name lookup ---------------------------------------------------------
  //
  // `getDocMeta` reads one record from the `docs` store and drops everything
  // but the metadata; unlike `listDocs()` it does not `getAll()` every
  // document, and unlike `getDoc()` it never touches the `binaries` store.
  useEffect(() => {
    if (!activeDocId) {
      setMeta(null);
      return undefined;
    }
    let cancelled = false;
    getDocMeta(activeDocId)
      .then((nextMeta) => {
        if (!cancelled) {
          setMeta(nextMeta ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMeta(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // `refreshToken` is the store's "docs changed" signal (save, switch,
    // create, delete, rename) — re-read the name whenever it moves.
  }, [activeDocId, refreshToken]);

  // Keep the old three-state name the render below depends on: `undefined`
  // while the lookup is still running, `null` once it found no record.
  const name: string | null | undefined =
    meta === undefined ? undefined : meta?.name ?? null;

  // --- upstream chrome visibility -----------------------------------------
  useEffect(() => {
    if (!excalidrawAPI) {
      return undefined;
    }
    const read = (appState: {
      zenModeEnabled?: boolean;
      viewModeEnabled?: boolean;
    }) => {
      const hidden = !!appState.zenModeEnabled || !!appState.viewModeEnabled;
      setChromeHidden((current) => (current === hidden ? current : hidden));
    };
    read(excalidrawAPI.getAppState());
    return excalidrawAPI.onChange((_elements, appState) => read(appState));
  }, [excalidrawAPI]);

  // --- rename --------------------------------------------------------------
  const startEditing = useCallback(() => {
    settledRef.current = false;
    setEditing(true);
  }, []);

  const cancelEditing = useCallback(() => {
    settledRef.current = true;
    setEditing(false);
  }, []);

  const commitRename = useCallback(
    async (nextName: string) => {
      if (settledRef.current) {
        return;
      }
      settledRef.current = true;
      setEditing(false);
      const trimmed = nextName.trim();
      if (!activeDocId || !trimmed || trimmed === name) {
        return;
      }
      try {
        await renameDoc(activeDocId, trimmed);
        setMeta((current) =>
          current ? { ...current, name: trimmed } : current,
        );
        // keep the Studio grid and the Files sidebar in sync
        onDocRenamed?.();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[drawOla] breadcrumb: rename failed", e);
      }
    },
    [activeDocId, name, onDocRenamed],
  );

  if (!activeDocId || chromeHidden || name === undefined) {
    return null;
  }

  const displayName = name ?? t("files.untitledPrefix");

  // A local-only draw has no binding, so its tooltip names the provider a
  // backup would go to. Ask the engine which provider this build ships rather
  // than hardcoding one; `undefined` when cloud storage is off.
  const firstProviderId = cloudSync?.engine.firstProviderId() ?? null;
  const defaultProviderName =
    cloudSync && firstProviderId
      ? cloudSync.engine.providerName(firstProviderId)
      : undefined;

  return (
    <div
      className={clsx("excalidraw", "drawola-breadcrumb", {
        "theme--dark": theme === THEME.DARK,
      })}
      // The editor keeps `handleKeyboardGlobally` listeners on `document`,
      // which sits above the app root React attaches to. Stopping propagation
      // here keeps breadcrumb keystrokes (notably every letter typed into the
      // rename input) out of the canvas shortcut handler.
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="drawola-breadcrumb__island">
        {onGoHome && (
          <>
            <button
              type="button"
              className="drawola-breadcrumb__home"
              aria-label={t("studio.breadcrumb.home")}
              title={t("studio.breadcrumb.home")}
              onClick={() => {
                void onGoHome();
              }}
            >
              {STUDIO_HOME_ICON}
            </button>
            <span className="drawola-breadcrumb__sep" aria-hidden="true">
              {CHEVRON_ICON}
            </span>
          </>
        )}
        {editing ? (
          <input
            className="drawola-breadcrumb__input"
            defaultValue={displayName}
            autoFocus
            aria-label={t("studio.breadcrumb.rename")}
            onFocus={(event) => event.currentTarget.select()}
            onBlur={(event) => commitRename(event.currentTarget.value)}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") {
                commitRename(event.currentTarget.value);
              } else if (event.key === "Escape") {
                cancelEditing();
              }
            }}
          />
        ) : (
          // a real <button>, so the name is keyboard-reachable
          <button
            type="button"
            className="drawola-breadcrumb__name"
            // the visible label is ellipsised, so the tooltip carries the full
            // name AND the (otherwise invisible) rename affordance
            title={t("studio.breadcrumb.renameTitle", { name: displayName })}
            onClick={startEditing}
          >
            {displayName}
          </button>
        )}
        {cloudSync && meta && (
          <span className="drawola-breadcrumb__sync">
            <SyncStatusIcon
              status={cloudSync.statusOf(meta)}
              providerName={cloudSync.providerName(meta)}
              defaultProviderName={defaultProviderName}
              theme={theme}
              onRepair={() => cloudSync.openRepair(meta.id)}
            />
          </span>
        )}
      </div>
    </div>
  );
};
